import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ShieldCheck, ArrowLeft, CheckCircle, Loader2, Search, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClaimPage() {
  const [mode, setMode] = useState('submit'); // submit | track
  const [form, setForm] = useState({
    customer_name: '', customer_phone: '', customer_email: '',
    device_imei: '', warranty_certificate: '', issue_description: '', issue_type: 'FUNCTIONAL_DEFECT',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const [trackId, setTrackId] = useState('');
  const [tracking, setTracking] = useState(false);
  const [trackResult, setTrackResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_phone || !form.device_imei || !form.issue_description) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/claims`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to submit claim');
      setSubmitted(data.claim_id);
      toast.success('Claim submitted successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackId) return;
    setTracking(true);
    try {
      const res = await fetch(`${API_URL}/api/claims/public/track?claim_id=${trackId}`);
      const data = await res.json();
      setTrackResult(data);
    } catch {
      toast.error('Failed to track claim');
    } finally {
      setTracking(false);
    }
  };

  const STATUS_LABELS = {
    SUBMITTED: { label: 'Submitted', color: 'bg-blue-100 text-blue-800' },
    UNDER_REVIEW: { label: 'Under Review', color: 'bg-amber-100 text-amber-800' },
    APPROVED: { label: 'Approved', color: 'bg-emerald-100 text-emerald-800' },
    REPAIR_IN_PROGRESS: { label: 'Repair in Progress', color: 'bg-purple-100 text-purple-800' },
    RESOLVED: { label: 'Resolved', color: 'bg-emerald-100 text-emerald-800' },
    REJECTED: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
  };

  const copyClaimId = () => {
    navigator.clipboard.writeText(submitted);
    toast.success('Claim ID copied!');
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {/* Navbar */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#005DE8] rounded-lg flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-lg text-slate-900">Mobikoo</span>
          </Link>
          <div className="flex gap-2">
            <Button variant={mode === 'submit' ? 'default' : 'outline'} size="sm" onClick={() => setMode('submit')} className={mode === 'submit' ? 'bg-[#005DE8]' : ''} data-testid="claim-tab-submit">File Claim</Button>
            <Button variant={mode === 'track' ? 'default' : 'outline'} size="sm" onClick={() => setMode('track')} className={mode === 'track' ? 'bg-[#005DE8]' : ''} data-testid="claim-tab-track">Track Claim</Button>
          </div>
        </div>
      </nav>

      <div className="max-w-xl mx-auto px-4 py-10">
        {mode === 'submit' ? (
          submitted ? (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-2">Claim Submitted</h2>
                <p className="text-slate-600 mb-4">Your claim has been received. Save your claim ID to track the status.</p>
                <div className="flex items-center justify-center gap-2 bg-slate-100 rounded-lg px-4 py-3 mb-6">
                  <span className="font-mono text-sm font-medium" data-testid="claim-id-display">{submitted}</span>
                  <Button size="sm" variant="ghost" onClick={copyClaimId} className="h-7 w-7 p-0" data-testid="copy-claim-id"><Copy className="h-4 w-4" /></Button>
                </div>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" onClick={() => { setSubmitted(null); setForm({ customer_name: '', customer_phone: '', customer_email: '', device_imei: '', warranty_certificate: '', issue_description: '', issue_type: 'FUNCTIONAL_DEFECT' }); }} data-testid="submit-another-claim">Submit Another</Button>
                  <Button onClick={() => { setMode('track'); setTrackId(submitted); }} className="bg-[#005DE8]" data-testid="track-this-claim">Track This Claim</Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit' }}>File a Warranty Claim</CardTitle>
                <CardDescription>Fill in the details below. We will process your claim within 48 hours.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Your Name *</Label>
                      <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} required data-testid="claim-name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone Number *</Label>
                      <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} required data-testid="claim-phone" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email (Optional)</Label>
                    <Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="claim-email" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Device IMEI *</Label>
                      <Input value={form.device_imei} onChange={(e) => setForm({ ...form, device_imei: e.target.value.replace(/\D/g, '').slice(0, 15) })} required placeholder="15-digit IMEI" data-testid="claim-imei" />
                    </div>
                    <div className="space-y-2">
                      <Label>Warranty Certificate #</Label>
                      <Input value={form.warranty_certificate} onChange={(e) => setForm({ ...form, warranty_certificate: e.target.value })} placeholder="WC-XXXX (optional)" data-testid="claim-certificate" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Issue Type *</Label>
                    <Select value={form.issue_type} onValueChange={(v) => setForm({ ...form, issue_type: v })}>
                      <SelectTrigger data-testid="claim-issue-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FUNCTIONAL_DEFECT">Functional Defect</SelectItem>
                        <SelectItem value="SCREEN_DAMAGE">Screen Damage</SelectItem>
                        <SelectItem value="LIQUID_DAMAGE">Liquid Damage</SelectItem>
                        <SelectItem value="OTHER">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Describe the Issue *</Label>
                    <Textarea value={form.issue_description} onChange={(e) => setForm({ ...form, issue_description: e.target.value })} rows={4} required placeholder="Describe what happened..." data-testid="claim-description" />
                  </div>
                  <Button type="submit" className="w-full h-11 bg-[#005DE8] hover:bg-[#004BBA]" disabled={submitting} data-testid="claim-submit-btn">
                    {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</> : 'Submit Claim'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )
        ) : (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit' }}>Track Your Claim</CardTitle>
              <CardDescription>Enter your claim ID to check the status</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTrack} className="space-y-4">
                <div className="flex gap-2">
                  <Input value={trackId} onChange={(e) => setTrackId(e.target.value)} placeholder="Enter claim ID" className="flex-1" data-testid="track-claim-input" />
                  <Button type="submit" disabled={tracking} className="bg-[#005DE8]" data-testid="track-claim-btn">
                    {tracking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
              </form>

              {trackResult && (
                <div className="mt-6">
                  {trackResult.found ? (
                    <div className="space-y-4 bg-slate-50 rounded-lg p-6" data-testid="track-result">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900">Claim Status</h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_LABELS[trackResult.claim.status]?.color || 'bg-slate-100'}`}>
                          {STATUS_LABELS[trackResult.claim.status]?.label || trackResult.claim.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-slate-500">Name:</span> <span className="font-medium">{trackResult.claim.customer_name}</span></div>
                        <div><span className="text-slate-500">IMEI:</span> <span className="font-mono">{trackResult.claim.device_imei}</span></div>
                        <div><span className="text-slate-500">Issue:</span> <span>{trackResult.claim.issue_type?.replace(/_/g, ' ')}</span></div>
                        <div><span className="text-slate-500">Submitted:</span> <span>{new Date(trackResult.claim.created_at).toLocaleDateString('en-IN')}</span></div>
                      </div>
                      {trackResult.claim.resolution_notes && (
                        <div className="border-t pt-3">
                          <p className="text-sm text-slate-500">Resolution Notes:</p>
                          <p className="text-sm mt-1">{trackResult.claim.resolution_notes}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-slate-50 rounded-lg" data-testid="track-not-found">
                      <p className="text-slate-600">No claim found with this ID. Please verify and try again.</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="mt-6 text-center">
          <Link to="/" className="inline-flex items-center text-sm text-[#005DE8] hover:underline">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
